import { useMemo } from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { useSocialMediaMetricsService, type SocialMediaDailyMetrics } from '@actschurch/ccms-sdk';

/**
 * A daily-metrics row. Aliased from the SDK model rather than redeclared, so backend
 * schema changes surface here as type errors instead of silently-missing fields.
 */
export type DailyMetricRow = SocialMediaDailyMetrics;
import { socialKeys } from '@/pages/app/SocialMediaManagement/utils/queryKeys';
import {
  aggregate, computeDelta, computeRate, rangeLengthDays,
  type AnalyticsFilter, type MetricDelta,
} from '@/pages/app/SocialMediaManagement/utils/analyticsHelpers';

export interface AnalyticsKpis {
  followers: MetricDelta;
  views: MetricDelta;
  viewers: MetricDelta;
  engagement: MetricDelta;
  engagementRate: MetricDelta;
  postsPublished: MetricDelta;
  netFollowerChange: MetricDelta;
}

/**
 * Daily metrics for the selected accounts, with period-over-period KPIs.
 *
 * Fetches the CURRENT and PREVIOUS windows as separate queries through the same key
 * builder. Doing the comparison client-side rather than via a backend `compare` flag
 * means toggling it is instant (both windows are already cached) and it works regardless
 * of whether the API ever grows that parameter.
 *
 * The metrics endpoint is per-account, so one query is issued per selected account and
 * the rows are concatenated. That is the shape the current API offers; a server-side
 * aggregate would replace this without changing the hook's contract.
 */
export function useSocialAnalytics(
  filter: AnalyticsFilter,
  comparisonFilter: AnalyticsFilter,
  accountIds: string[],
) {
  const metricsService = useSocialMediaMetricsService();

  const targetIds = filter.accountIds.length > 0 ? filter.accountIds : accountIds;

  const currentQueries = useQueries({
    queries: targetIds.map((id) => ({
      queryKey: [...socialKeys.dailyMetrics(filter), id],
      queryFn: () => metricsService.getDailyMetrics(id, filter.from, filter.to),
      staleTime: 5 * 60 * 1000,
      // Keeps the previous window's charts on screen while a new range loads, instead
      // of blanking a dozen charts at once.
      placeholderData: keepPreviousData,
      enabled: targetIds.length > 0,
    })),
  });

  const previousQueries = useQueries({
    queries: (filter.compareToPrevious ? targetIds : []).map((id) => ({
      queryKey: [...socialKeys.dailyMetrics(comparisonFilter), id],
      queryFn: () => metricsService.getDailyMetrics(id, comparisonFilter.from, comparisonFilter.to),
      staleTime: 5 * 60 * 1000,
      placeholderData: keepPreviousData,
    })),
  });

  const isLoading = currentQueries.some((q) => q.isLoading);
  const isFetching = currentQueries.some((q) => q.isFetching) || previousQueries.some((q) => q.isFetching);
  const isError = currentQueries.some((q) => q.isError);

  const rows = useMemo(
    () => currentQueries.flatMap((q) => (q.data ?? []) as DailyMetricRow[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentQueries.map((q) => q.dataUpdatedAt).join(',')],
  );

  const previousRows = useMemo(
    () => previousQueries.flatMap((q) => (q.data ?? []) as DailyMetricRow[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousQueries.map((q) => q.dataUpdatedAt).join(',')],
  );

  const kpis = useMemo<AnalyticsKpis>(() => {
    const has = filter.compareToPrevious && previousRows.length > 0;

    // Follower count is a LEVEL, not a flow: 'last' rather than 'sum'. Summing it over
    // 28 days would report 28x the actual audience.
    const followers = aggregate(rows, 'followerCount', 'last');
    const prevFollowers = has ? aggregate(previousRows, 'followerCount', 'last') : undefined;

    return {
      followers: computeDelta(followers, prevFollowers),
      views: computeDelta(
        aggregate(rows, 'mediaViews') + aggregate(rows, 'totalViews'),
        has ? aggregate(previousRows, 'mediaViews') + aggregate(previousRows, 'totalViews') : undefined,
      ),
      viewers: computeDelta(
        aggregate(rows, 'mediaViewers') + aggregate(rows, 'totalReach'),
        has ? aggregate(previousRows, 'mediaViewers') + aggregate(previousRows, 'totalReach') : undefined,
      ),
      engagement: computeDelta(
        aggregate(rows, 'totalEngagement'),
        has ? aggregate(previousRows, 'totalEngagement') : undefined,
      ),
      // A rate must be recomputed from the summed parts, never averaged across days.
      engagementRate: computeDelta(
        computeRate(rows, 'totalEngagement', 'mediaViewers'),
        has ? computeRate(previousRows, 'totalEngagement', 'mediaViewers') : undefined,
      ),
      postsPublished: computeDelta(
        aggregate(rows, 'postsPublished'),
        has ? aggregate(previousRows, 'postsPublished') : undefined,
      ),
      netFollowerChange: computeDelta(
        aggregate(rows, 'netFollowerChange'),
        has ? aggregate(previousRows, 'netFollowerChange') : undefined,
      ),
    };
  }, [rows, previousRows, filter.compareToPrevious]);

  return {
    rows,
    previousRows,
    kpis,
    isLoading,
    isFetching,
    isError,
    isEmpty: !isLoading && rows.length === 0,
    periodDays: rangeLengthDays(filter),
  };
}
